import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useBackground} from "../../../../hooks/background-class"
import iconSave from "../../../../assets/images/icons/media/save.png"
import iconCancel from "../../../../assets/images/icons/media/cancel.png"
import iconRemove from "../../../../assets/images/icons/scenario/clear-2.png"
import iconPlus from "../../../../assets/images/icons/controls/plus.png"
import iconTrash from "../../../../assets/images/icons/trash.png"
import iconEye from "../../../../assets/images/icons/eye.png"
import iconPlay from "../../../../assets/images/icons/controls/play.png"
import iconClock from "../../../../assets/images/icons/clock.png"
import icomTemp from "../../../../assets/images/icons/temperature.png"
import {useKeyboard} from "../../../../hooks/keyboard"
import {onClickHandler} from "../../../../utils/onclick"
import {useHistory, useLocation, useParams} from "react-router-dom"
import {useSelector} from "react-redux"
import {
    CONTROL_TYPE_GENERAL_TEMPERATURE,
    CONTROL_TYPE_ID_ALARM,
    CameraModel,
    ControlModel,
    SOUND_LEVEL_TYPE_ID,
    SOUND_PLAYED_TYPE_ID
} from "../../../../types/control"
import {AreaModel, AuthorityModel, MenuEntity} from "../../../../store/control/state"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useQuestion} from "../../../../hooks/question"
import {useLocalization} from "../../../../hooks/localization"
import "./index.scss"
import {isValidTemperature, isValidTime} from "../../../../utils/validation"

export const ComplexButton = memo(function ComplexButton({
    isSelected = false,
    title,
    onClick,
    type,
    position
}: {
    isSelected: boolean
    title: string
    onClick?: () => void
    type: "radio" | "checkbox"
    position: "center" | "start"
}) {
    return (
        <div className={`auth-radio-button-container ${position}`} onClick={onClick}>
            {renderIf(position === "center")(
                () => (
                    <>
                        <span>{title}</span>
                        <div
                            className={`auth-radio-pointer ${isSelected ? "radio-selected" : ""}`}
                            style={{borderRadius: `${type === "radio" ? "50%" : type === "checkbox" ? "3px" : "50%"}`}}
                        />
                    </>
                ),
                () => (
                    <>
                        <div
                            className={`auth-radio-pointer ${isSelected ? "radio-selected" : ""}`}
                            style={{borderRadius: `${type === "radio" ? "50%" : type === "checkbox" ? "3px" : "50%"}`}}
                        />
                        <span>{title}</span>
                    </>
                )
            )}
        </div>
    )
})
const formatTime = (value: string): string => {
    const cleanedValue = value.replace(/[^\d]/g, "")
    if (cleanedValue === "" || cleanedValue.length > 4) {
        return cleanedValue
    }
    let hours = cleanedValue.substring(0, 2)
    let minutes = cleanedValue.substring(2, 4)
    if (parseInt(hours, 10) > 23) {
        hours = "23"
    }
    if (parseInt(minutes, 10) > 59) {
        minutes = "59"
    }
    return `${hours}:${minutes}`
}
const formatTemp = (value: string): string => {
    const cleanedValue = value.replace(/[^\d]/g, "")
    if (cleanedValue === "" || cleanedValue.length > 3) {
        return cleanedValue
    }
    let a = cleanedValue.substring(0, 2)
    let b = cleanedValue.substring(2, 3)
    return `${a}.${b}`
}

type QueryType = "control" | "camera" | "area" | "menu"
type AuthorityType = "execute" | "view"

type ComplexListType<T = unknown> = {
    id: string
    name: string
    type: QueryType
    authority?: AuthorityType
    values: T[]
}
const dayControl = (list: number[], num: number) => {
    return list.includes(num) ? true : false
}
const complexListOptions = <T extends {id?: string; name: string}>(
    control: T[],
    type: QueryType,
    authority: AuthorityType
) => {
    const newList = []
    for (const values of control) {
        newList.push({
            id: values?.id,
            name: values.name,
            type: type,
            authority: authority,
            values: values
        })
    }
    return newList as ComplexListType<T>[]
}

const findByDeepKey = (array: ComplexListType<unknown>[], selected: AuthorityModel, list: string, list_key: string) =>
    array.filter(r => (selected[list][list_key].includes(r.id) ? r.id : null))

const findByKey = (array: ComplexListType<unknown>[], selected: AuthorityModel, list: string) =>
    array.filter(r => (selected[list].includes(r.id) ? r.id : null))

export const AuthorizationGroupScreen = memo(function AuthorizationGroupScreen() {
    const [rpc] = useRPC()
    const {failure, success} = useMessage()
    const params = useParams<{id: string}>()
    const location = useLocation<AuthorityModel>()
    const {t} = useLocalization()
    const question = useQuestion()
    const authoritySet = useSetSelector(state => state.control.authorityGroupList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || authoritySet[params.id] || null, [])
    const [notification, setNotification] = useState(selected == null ? true : selected?.parameters?.is_notification)
    const [alarmPassive, setAlarmPassive] = useState(selected == null ? true : selected?.parameters?.can_disable_alarm)
    const [monday, setMonday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 1))
    const [tuesday, setTuesday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 2))
    const [wednesday, setWednesday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 3))
    const [thursday, setThursday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 4))
    const [friday, setFriday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 5))
    const [saturday, setSaturday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 6))
    const [sunday, setSunday] = useState(selected == null ? true : dayControl(selected?.parameters?.day_list, 0))
    const [checkControls, setCheckControls] = useState(true)
    const [checkCameras, setCheckCameras] = useState(false)
    const [checkRooms, setCheckRooms] = useState(false)
    const [checkLeftMenu, setCheckLeftMenu] = useState(false)
    const [checkVisible, setCheckVisible] = useState(true)
    const [checkCanRun, setCheckCanRun] = useState(false)
    const [, , backgroundStyle] = useBackground()
    const searchRef = useRef<HTMLInputElement>()
    const [setActiveInput, renderKeyboard] = useKeyboard()
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard] = useKeyboard("numpad-extended", true)
    const [name, setName] = useState<string>((selected == null ? null : selected.name) || "")
    const history = useHistory()
    const onBlur = useCallback(() => setActiveInput(null), [setActiveInput])
    const onFocus = useCallback(() => setActiveInput([searchRef, name, setName]), [setActiveInput, name])
    const beforeTimeRef = useRef<HTMLInputElement>()
    const [beforeTimeStr, setBeforeTimeStr] = useState(selected == null ? "" : selected?.parameters?.before_time)
    const afterTimeRef = useRef<HTMLInputElement>()
    const [afterTimeStr, setAfterTimeStr] = useState(selected == null ? "" : selected?.parameters?.after_time)
    const minTempRef = useRef<HTMLInputElement>()
    const [minTempStr, setMinTempStr] = useState(selected == null ? "" : selected?.parameters?.min_temp)
    const maxTempRef = useRef<HTMLInputElement>()
    const [maxTempStr, setMaxTempStr] = useState(selected == null ? "" : selected?.parameters?.max_temp)

    const onClickReturnBack = useCallback(() => history.push("/settings/authority"), [history])

    const onChangeMinTemp = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const formattedTime = formatTemp(inputValue)
        setMinTempStr(formattedTime)
    }, [])
    const onFocusMinTemp = useCallback(
        () => setActiveNumpadExtendedInput([minTempRef, minTempStr, setMinTempStr]),
        [setActiveNumpadExtendedInput, minTempStr]
    )

    const onChangeMaxTemp = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const formattedTime = formatTemp(inputValue)
        setMaxTempStr(formattedTime)
    }, [])
    const onFocusMaxTemp = useCallback(
        () => setActiveNumpadExtendedInput([maxTempRef, maxTempStr, setMaxTempStr]),
        [setActiveNumpadExtendedInput, maxTempStr]
    )

    const onChangeBeforeTime = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const formattedTime = formatTime(inputValue)
        setBeforeTimeStr(formattedTime)
    }, [])

    const onFocusBeforeTime = useCallback(
        () => setActiveNumpadExtendedInput([beforeTimeRef, beforeTimeStr, setBeforeTimeStr]),
        [setActiveNumpadExtendedInput, beforeTimeStr]
    )

    const onChangeAfterTime = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const formattedTime = formatTime(inputValue)
        setAfterTimeStr(formattedTime)
    }, [])

    const onFocusAfterTime = useCallback(
        () => setActiveNumpadExtendedInput([afterTimeRef, afterTimeStr, setAfterTimeStr]),
        [setActiveNumpadExtendedInput, afterTimeStr]
    )

    const onBlurNumber = useCallback(() => setActiveNumpadExtendedInput(null), [setActiveNumpadExtendedInput])

    const onClickComplexOptions = useCallback((which: number) => {
        switch (which) {
            case 1:
                setCheckControls(true)
                setCheckCameras(false)
                setCheckRooms(false)
                setCheckLeftMenu(false)
                return
            case 2:
                setCheckControls(false)
                setCheckCameras(true)
                setCheckRooms(false)
                setCheckLeftMenu(false)
                return
            case 3:
                setCheckControls(false)
                setCheckCameras(false)
                setCheckRooms(true)
                setCheckLeftMenu(false)
                return
            case 4:
                setCheckControls(false)
                setCheckCameras(false)
                setCheckRooms(false)
                setCheckLeftMenu(true)
                return
        }
    }, [])
    const [authority, setAuthority] = useState<AuthorityType>("view")

    const onClickAuthOptions = useCallback((which: number) => {
        switch (which) {
            case 1:
                setCheckVisible(true)
                setCheckCanRun(false)
                setAuthority("view")
                return
            case 2:
                setCheckVisible(false)
                setCheckCanRun(true)
                setAuthority("execute")
                return
        }
    }, [])

    const controlList = useSelector(state => state.control.controlList).filter(
        control =>
            control.type_id !== CONTROL_TYPE_ID_ALARM &&
            control.type_id !== SOUND_LEVEL_TYPE_ID &&
            control.type_id !== SOUND_PLAYED_TYPE_ID &&
            control.type_id !== CONTROL_TYPE_GENERAL_TEMPERATURE
    )
    const cameraList = useSelector(state => state.control.cameraList)
    const areaList = useSelector(state => state.control.areaList)
    const menuList = useSelector(state => state.control.menuList).filter(
        r => r.is_special && r.id !== "settings" && r.id !== "area" && r.id !== "all-controls"
    )

    const controlListOptions = useMemo(
        () => complexListOptions<ControlModel>(controlList, "control", authority),
        [authority, controlList]
    )
    const cameraListOptions = useMemo(
        () => complexListOptions<CameraModel>(cameraList, "camera", authority),
        [authority, cameraList]
    )
    const areaListOptions = useMemo(
        () => complexListOptions<AreaModel>(areaList, "area", authority),
        [authority, areaList]
    )
    const menuListOptions = useMemo(
        () => complexListOptions<MenuEntity>(menuList, "menu", authority),
        [authority, menuList]
    )

    const allListControl = useMemo(
        () => [...controlListOptions, ...cameraListOptions, ...areaListOptions, ...menuListOptions],
        [controlListOptions, cameraListOptions, areaListOptions, menuListOptions]
    )

    const selectedList = useMemo(() => {
        if (selected?.id == null) {
            return null
        }
        const setAuthority = (authority: AuthorityType) =>
            allListControl.map(r => {
                return {...r, authority: authority}
            })
        const datas = [
            ...findByDeepKey(setAuthority("execute"), selected, "area_list", "executable"),
            ...findByDeepKey(setAuthority("view"), selected, "area_list", "viewable"),
            ...findByDeepKey(setAuthority("execute"), selected, "control_id_list", "executable"),
            ...findByDeepKey(setAuthority("view"), selected, "control_id_list", "viewable"),
            ...findByKey(setAuthority("view"), selected, "menu_list"),
            ...findByKey(setAuthority("view"), selected, "camera_list")
        ]
        return datas
    }, [selected, allListControl])

    const filteredSelectedList = useMemo(
        () => (selected?.id == null ? [] : selectedList.map(r => r?.id)),
        [selected?.id, selectedList]
    )
    const filteredAllList = useMemo(
        () => allListControl.filter(r => !filteredSelectedList.includes(r?.id)),
        [allListControl, filteredSelectedList]
    )

    const [allList, setAllList] = useState<ComplexListType[]>(selected == null ? allListControl : filteredAllList)

    const [actionList, setActionList] = useState<ComplexListType[]>(selected == null ? [] : selectedList)

    //const [removedParentArea, setRemovedParentArea] = useState<ComplexListType[]>([])
    //const [addedParentArea, setAddedParentArea] = useState<ComplexListType[]>([])

    const onClickSelect = useCallback(
        (selected: ComplexListType) => {
            /*setAddedParentArea(p => [
                ...p,
                ...[...allList, ...removedParentArea].filter(
                    r => r.type === "area" && r.values["parent_area_id"] === selected.id
                )
            ])
            setRemovedParentArea(p => p.filter(r => r.type === "area" && r.values["parent_area_id"] !== selected.id))*/

            setAllList(p => [...p.filter(i => i.id !== selected.id /*&& i.values["parent_area_id"] !== selected.id*/)])
            setActionList(
                p => [
                    ...p,
                    {...selected, authority: authority}
                ] /*.filter(i => i.values["parent_area_id"] !== selected.id)*/
            )
        },
        [authority /*allList,removedParentArea*/]
    )

    const onClickRemove = useCallback(
        (id: string) => {
            /*setAddedParentArea(p => p.filter(r => r.type === "area" && r.values["parent_area_id"] !== id))
            setRemovedParentArea(p => [
                ...p,
                ...addedParentArea.filter(r => r.type === "area" && r.values["parent_area_id"] === id)
            ])*/

            setActionList(p => p.filter(i => i.id !== id))
            setAllList(p => [...p, actionList.find(v => v.id === id)])
        },
        [actionList /*addedParentArea*/]
    )

    //console.log(addedParentArea, removedParentArea)

    const isValid = useCallback((): boolean => {
        if (name.length === 0) {
            failure(t("authority.pls_enter_an_authorization_group_name"))
            return false
        }
        if (actionList.length === 0) {
            failure(t("authority.at_least_one_option_must_be_added"))
            return false
        }
        if (
            (beforeTimeStr.length >= 1 && beforeTimeStr.length <= 4) ||
            (afterTimeStr.length >= 1 && afterTimeStr.length <= 4) ||
            (beforeTimeStr.length > 0 && afterTimeStr.length === 0) ||
            (afterTimeStr.length > 0 && beforeTimeStr.length === 0)
        ) {
            failure(t("authority.enter_a_valid_time_value"))
            return false
        }
        if (beforeTimeStr.length > 0 && afterTimeStr.length > 0) {
            if (!isValidTime(afterTimeStr) || !isValidTime(beforeTimeStr)) {
                failure(t("authority.enter_a_valid_time_value"))
                return false
            }
        }
        if (
            (minTempStr.length >= 1 && minTempStr.length <= 3) ||
            (maxTempStr.length >= 1 && maxTempStr.length <= 3) ||
            (minTempStr.length > 0 && maxTempStr.length === 0) ||
            (maxTempStr.length > 0 && minTempStr.length === 0)
        ) {
            failure(t("authority.enter_a_valid_temperature_value"))
            return false
        }
        if (maxTempStr.length > 0 && minTempStr.length > 0 && maxTempStr <= minTempStr) {
            failure(t("authority.the_maximum_temperature_value_cannot_be_less_than_or_equal_to_the_minimum_value"))
            return false
        }
        if (maxTempStr.length > 0 && minTempStr.length > 0) {
            if (!isValidTemperature(maxTempStr) || !isValidTemperature(minTempStr)) {
                failure(t("authority.enter_a_valid_temperature_value"))
                return false
            }
        }
        return true
    }, [t, failure, name, actionList, beforeTimeStr, afterTimeStr, maxTempStr, minTempStr])

    const dayListControl = useMemo(() => {
        const days = [sunday, monday, tuesday, wednesday, thursday, friday, saturday]
            .map((d, i) => (d ? i : null))
            .filter(d => d !== null)

        return days
    }, [sunday, monday, tuesday, wednesday, thursday, friday, saturday])

    const queryList = useCallback(
        (query: QueryType, authority?: AuthorityType) => {
            if (query === "menu" || query === "camera") {
                return actionList.map(r => (r.type === query ? r.id : "")).filter(r => r !== "")
            } else {
                return actionList
                    .map(r => (r.type === query && r.authority === authority ? r.id : ""))
                    .filter(r => r !== "")
            }
        },
        [actionList]
    )
    const onClickSaveAuthority = useCallback(() => {
        if (!isValid()) {
            return false
        }

        const saveAuthority: AuthorityModel = {
            ...(selected ? selected : {}),
            id: selected?.id || null,
            name: name,
            control_id_list: {
                executable: queryList("control", "execute"),
                viewable: queryList("control", "view")
            },
            camera_list: queryList("camera"),
            menu_list: queryList("menu"),
            area_list: {
                executable: queryList("area", "execute"),
                viewable: queryList("area", "view")
            },
            parameters: {
                after_time: afterTimeStr,
                before_time: beforeTimeStr,
                can_disable_alarm: alarmPassive,
                day_list: dayListControl,
                is_notification: notification,
                max_temp: maxTempStr,
                min_temp: minTempStr
            }
        }
        return rpc
            .updateAuthorityGroup(saveAuthority)
            .then(() => {
                success(
                    selected?.id == null
                        ? t("authority.adding_an_authorization_group_is_successful")
                        : t("authority.editing_an_authorization_group_is_successful")
                )
                onClickReturnBack()
            })
            .catch(failure)
    }, [
        t,
        isValid,
        failure,
        success,
        queryList,
        onClickReturnBack,
        afterTimeStr,
        maxTempStr,
        minTempStr,
        beforeTimeStr,
        selected,
        rpc,
        name,
        notification,
        alarmPassive,
        dayListControl
    ])

    const onClickDelete = useCallback(
        (id: string) => {
            question(t("are_you_sure"), () => {
                return rpc
                    .deleteAuthorityGroup(id)
                    .then(() => {
                        success(t("authority.user_authorization_deleted_successfully"))
                        onClickReturnBack()
                    })
                    .catch(failure)
            })
        },
        [t, question, success, failure, onClickReturnBack, rpc]
    )

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <div className="auth-full-container" style={{backgroundImage: backgroundStyle?.backgroundImage}}>
                <div className="auth-container">
                    <div className="auth-top-container">
                        <div className="auth-top-field">
                            <span>
                                <input
                                    type="text"
                                    maxLength={25}
                                    placeholder={t("authority.authorization_group_name")}
                                    ref={searchRef}
                                    onChange={e => setName(e.target.value)}
                                    onFocus={onFocus}
                                    onBlur={onBlur}
                                    value={name}
                                />
                            </span>
                            <span>
                                {renderIf(selected?.id != null)(() => (
                                    <div className="with-text-button">
                                        <img
                                            src={iconRemove}
                                            alt=""
                                            {...onClickHandler(() => onClickDelete(selected.id))}
                                        />
                                        <div className="_txt">{t("delete")}</div>
                                    </div>
                                ))}
                                <div className="with-text-button">
                                    <img src={iconCancel} alt="" onClick={onClickReturnBack} />
                                    <div className="_txt">{t("cancel")}</div>
                                </div>
                                <div className="with-text-button">
                                    <img src={iconSave} alt="" {...onClickHandler(onClickSaveAuthority)} />
                                    <div className="_txt">{t("save")}</div>
                                </div>
                            </span>
                        </div>
                        <div className="auth-top-actions">
                            <span className="border-line">
                                <ComplexButton
                                    isSelected={notification}
                                    title={t("authority.notification")}
                                    onClick={() => setNotification(p => !p)}
                                    position="start"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={alarmPassive}
                                    title={t("authority.disable_alarm")}
                                    onClick={() => setAlarmPassive(p => !p)}
                                    position="start"
                                    type="checkbox"
                                />
                            </span>
                            <span>
                                <ComplexButton
                                    isSelected={monday}
                                    title={t("authority.monday")}
                                    onClick={() => setMonday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={tuesday}
                                    title={t("authority.tuesday")}
                                    onClick={() => setTuesday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={wednesday}
                                    title={t("authority.wednesday")}
                                    onClick={() => setWednesday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={thursday}
                                    title={t("authority.thursday")}
                                    onClick={() => setThursday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={friday}
                                    title={t("authority.friday")}
                                    onClick={() => setFriday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={saturday}
                                    title={t("authority.saturday")}
                                    onClick={() => setSaturday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                                <ComplexButton
                                    isSelected={sunday}
                                    title={t("authority.sunday")}
                                    onClick={() => setSunday(p => !p)}
                                    position="center"
                                    type="checkbox"
                                />
                            </span>
                        </div>
                        <div className="auth-top-actions">
                            <span className="border-line">
                                <ComplexButton
                                    isSelected={checkControls}
                                    title={t("authority.controls")}
                                    onClick={() => onClickComplexOptions(1)}
                                    position="center"
                                    type="radio"
                                />
                                <ComplexButton
                                    isSelected={checkCameras}
                                    title={t("authority.cameras")}
                                    onClick={() => onClickComplexOptions(2)}
                                    position="center"
                                    type="radio"
                                />
                                <ComplexButton
                                    isSelected={checkRooms}
                                    title={t("authority.floors")}
                                    onClick={() => onClickComplexOptions(3)}
                                    position="center"
                                    type="radio"
                                />
                                <ComplexButton
                                    isSelected={checkLeftMenu}
                                    title={t("authority.left_menu")}
                                    onClick={() => onClickComplexOptions(4)}
                                    position="center"
                                    type="radio"
                                />
                            </span>
                            <span>
                                <div className="auth-value-field">
                                    <div className="content-wrapper">
                                        <p>{t("authority.after")}</p>
                                        <input
                                            maxLength={5}
                                            type="text"
                                            ref={afterTimeRef}
                                            onBlur={onBlurNumber}
                                            onChange={onChangeAfterTime}
                                            onFocus={onFocusAfterTime}
                                            value={afterTimeStr}
                                            placeholder="--:--"
                                        />
                                        <img src={iconClock} alt="" />
                                    </div>
                                    <div className="content-wrapper">
                                        <p>{t("authority.before")}</p>
                                        <input
                                            maxLength={5}
                                            type="text"
                                            ref={beforeTimeRef}
                                            onBlur={onBlurNumber}
                                            onChange={onChangeBeforeTime}
                                            onFocus={onFocusBeforeTime}
                                            value={beforeTimeStr}
                                            placeholder="--:--"
                                        />
                                        <img src={iconClock} alt="" />
                                    </div>
                                </div>
                                <div className="auth-value-field">
                                    <div className="content-wrapper">
                                        <p>Min °C</p>
                                        <input
                                            maxLength={4}
                                            type="text"
                                            ref={minTempRef}
                                            onBlur={onBlurNumber}
                                            onChange={onChangeMinTemp}
                                            onFocus={onFocusMinTemp}
                                            value={minTempStr}
                                            placeholder="--.-"
                                        />
                                        <img src={icomTemp} alt="" style={{width: "40px", marginLeft: "0px"}} />
                                    </div>
                                    <div className="content-wrapper">
                                        <p>Max °C</p>
                                        <input
                                            maxLength={4}
                                            type="text"
                                            ref={maxTempRef}
                                            onBlur={onBlurNumber}
                                            onChange={onChangeMaxTemp}
                                            onFocus={onFocusMaxTemp}
                                            value={maxTempStr}
                                            placeholder="--.-"
                                        />
                                        <img src={icomTemp} alt="" style={{width: "40px", marginLeft: "0"}} />
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div className="auth-top-actions">
                            <span className={checkControls || checkRooms ? "border-line" : ""}>
                                {renderIf(checkControls || checkRooms)(() => (
                                    <>
                                        <ComplexButton
                                            isSelected={checkVisible}
                                            title={t("authority.can_see")}
                                            onClick={() => onClickAuthOptions(1)}
                                            position="center"
                                            type="radio"
                                        />
                                        <ComplexButton
                                            isSelected={checkCanRun}
                                            title={t("authority.can_run")}
                                            onClick={() => onClickAuthOptions(2)}
                                            position="center"
                                            type="radio"
                                        />
                                    </>
                                ))}
                            </span>
                            <span />
                        </div>
                    </div>
                    <div className="auth-bottom-container">
                        <div className="auth-bottom-lr-container">
                            <div className="auth-bottom-lr">
                                {renderIf(checkControls)(() =>
                                    allList.map(
                                        control =>
                                            control.type === "control" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <div
                                                            onClick={() => onClickSelect(control)}
                                                            className="auth-add-text"
                                                        >
                                                            {t("add")}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                                {renderIf(checkCameras)(() =>
                                    allList.map(
                                        control =>
                                            control.type === "camera" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <div
                                                            onClick={() => onClickSelect(control)}
                                                            className="auth-add-text"
                                                        >
                                                            {t("add")}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                                {renderIf(checkRooms)(() =>
                                    allList.map(
                                        control =>
                                            control.type === "area" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <div
                                                            onClick={() => onClickSelect(control)}
                                                            className="auth-add-text"
                                                        >
                                                            {t("add")}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                                {renderIf(checkLeftMenu)(() =>
                                    allList.map(
                                        control =>
                                            control.type === "menu" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <div
                                                            onClick={() => onClickSelect(control)}
                                                            className="auth-add-text"
                                                        >
                                                            {t("add")}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                            </div>
                        </div>
                        <div className="auth-bottom-lr-container">
                            <div className="auth-bottom-lr">
                                {renderIf(checkControls)(() =>
                                    actionList.map(
                                        control =>
                                            control.type === "control" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <div className="auth-icon-box">
                                                        <img
                                                            src={control.authority === "execute" ? iconPlay : iconEye}
                                                            alt=""
                                                        />
                                                    </div>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <img
                                                            src={iconTrash}
                                                            alt=""
                                                            onClick={() => onClickRemove(control.id)}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                                {renderIf(checkCameras)(() =>
                                    actionList.map(
                                        control =>
                                            control.type === "camera" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <div className="auth-icon-box">
                                                        <img src={iconEye} alt="" />
                                                    </div>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <img
                                                            src={iconTrash}
                                                            alt=""
                                                            onClick={() => onClickRemove(control.id)}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                                {renderIf(checkRooms)(() =>
                                    actionList.map(
                                        control =>
                                            control.type === "area" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <div className="auth-icon-box">
                                                        <img
                                                            src={control.authority === "execute" ? iconPlay : iconEye}
                                                            alt=""
                                                        />
                                                    </div>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <img
                                                            src={iconTrash}
                                                            alt=""
                                                            onClick={() => onClickRemove(control.id)}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                                {renderIf(checkLeftMenu)(() =>
                                    actionList.map(
                                        control =>
                                            control.type === "menu" && (
                                                <div className="auth-bottom-option" key={control.id}>
                                                    <div className="auth-icon-box">
                                                        <img src={iconEye} alt="" />
                                                    </div>
                                                    <span>{control.name}</span>
                                                    <div className="auth-icon-box">
                                                        <img
                                                            src={iconTrash}
                                                            alt=""
                                                            onClick={() => onClickRemove(control.id)}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {renderKeyboard()}
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
